import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CompletedIcon,
  PendingActions as PendingIcon,
  PlayCircleFilled as StartedIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  QrCode as QrCodeIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Today as TodayIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// TaskCard bileşeni için stil tanımı
const TaskCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  display: 'flex',
  flexDirection: 'column',
}));

// Görev zamanı chip bileşeni
const TaskTimeChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  backgroundColor: `${status}20`, // Renkli arkaplan
  color: status,
  fontSize: '0.7rem',
  height: 20
}));

// TaskGrid bileşeni için props arayüzü
interface TaskGridProps {
  tasks: any[];
  onShowTaskDetail: (task: any) => void;
  onOpenQrPrintModal: (task: any, event: React.MouseEvent) => void;
  getStatusColor: (status: string) => string;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusLabel: (status: string) => string;
  onOpenDeviceModal?: (deviceInfo: any) => void;
  taskType: string;
  getTaskTimeColor?: (task: any, timeString: string) => string;
  branches?: {[key: string]: string}; // Şube bilgilerini içeren prop
  onRenewTask?: (taskId: string, adminNote: string) => Promise<void>; // Görevi yenileme fonksiyonu
}

// TaskGrid bileşeni
const TaskGrid: React.FC<TaskGridProps> = ({
  tasks,
  onShowTaskDetail,
  onOpenQrPrintModal,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  onOpenDeviceModal,
  taskType,
  getTaskTimeColor,
  branches,
  onRenewTask
}) => {
  const { t } = useTranslation('common2');
  
  // Gecikme açıklaması modalı için state
  const [delayReasonModalOpen, setDelayReasonModalOpen] = useState(false);
  const [selectedDelayReason, setSelectedDelayReason] = useState<string>("");
  
  // Red nedeni modalı için state
  const [rejectReasonModalOpen, setRejectReasonModalOpen] = useState(false);
  const [selectedRejectReason, setSelectedRejectReason] = useState<string>("");

  // Görev yenileme modalı için state
  const [renewTaskModalOpen, setRenewTaskModalOpen] = useState(false);
  const [selectedTaskToRenew, setSelectedTaskToRenew] = useState<any>(null);
  const [adminNote, setAdminNote] = useState<string>("");
  const [isRenewing, setIsRenewing] = useState(false);
  
  // Gecikme açıklaması modalını açma işlevi
  const handleOpenDelayReasonModal = (delayReason: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Tıklamanın üst öğelere geçmesini engelle
    setSelectedDelayReason(delayReason);
    setDelayReasonModalOpen(true);
  };
  
  // Gecikme açıklaması modalını kapatma işlevi
  const handleCloseDelayReasonModal = () => {
    setDelayReasonModalOpen(false);
  };

  // Red nedeni modalını açma işlevi
  const handleOpenRejectReasonModal = (rejectReason: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Tıklamanın üst öğelere geçmesini engelle
    setSelectedRejectReason(rejectReason);
    setRejectReasonModalOpen(true);
  };

  // Red nedeni modalını kapatma işlevi
  const handleCloseRejectReasonModal = () => {
    setRejectReasonModalOpen(false);
  };

  // Görev yenileme modalını açma işlevi
  const handleOpenRenewTaskModal = (task: any, event: React.MouseEvent) => {
    event.stopPropagation(); // Tıklamanın üst öğelere geçmesini engelle
    setSelectedTaskToRenew(task);
    setAdminNote("");
    setRenewTaskModalOpen(true);
  };

  // Görev yenileme modalını kapatma işlevi
  const handleCloseRenewTaskModal = () => {
    setRenewTaskModalOpen(false);
    setSelectedTaskToRenew(null);
    setAdminNote("");
  };

  // Görevi yenileme işlevi
  const handleRenewTask = async () => {
    if (!selectedTaskToRenew || !onRenewTask) return;
    
    try {
      setIsRenewing(true);
      await onRenewTask(selectedTaskToRenew.id, adminNote);
      handleCloseRenewTaskModal();
    } catch (error) {
      console.error('Görev yenilenirken hata:', error);
    } finally {
      setIsRenewing(false);
    }
  };
  
  // Şube adını getir
  const getBranchName = (branchId: string | null) => {
    if (!branchId) return '-';
    
    // branchId bir nesne mi kontrol et ve düzenle
    if (typeof branchId === 'object' && branchId !== null) {
      const keys = Object.keys(branchId);
      if (keys.length > 0) {
        const firstKey = keys[0];
        branchId = firstKey;
      }
    }
    
    // Artık branchId bir string olmalı, şube adını döndür
    return branches && branches[branchId as string] ? branches[branchId as string] : branchId;
  };
  
  return (
    <>
      <Grid container spacing={3}>
        {tasks.map((task) => (
          <Grid item xs={12} sm={6} md={3} key={task.id}>
            <TaskCard 
              onClick={() => onShowTaskDetail(task)}
              sx={{
                cursor: task.status === 'completed' || task.status === 'missed' ? 'default' : 'pointer',
                '&:hover': {
                  boxShadow: task.status === 'completed' || task.status === 'missed' 
                    ? '0 4px 12px rgba(0,0,0,0.05)' // Orijinal gölge
                    : '0 6px 16px rgba(0,0,0,0.1)' // Hover durumunda daha belirgin gölge
                }
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${getStatusColor(task.status)}20`,
                      color: getStatusColor(task.status),
                      mr: 1,
                    }}
                  >
                    {getStatusIcon(task.status)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                      {task.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(task.status)}
                      size="small"
                      sx={{
                        bgcolor: `${getStatusColor(task.status)}20`,
                        color: getStatusColor(task.status),
                        fontWeight: 'medium',
                        height: 20,
                        fontSize: 11,
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                                  <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flexGrow: 1,
                  }}
                >
                  {task.description || t('tasks.noDescription', 'Açıklama yok')}
                </Typography>
                
                {/* Bitiş tarihi gösterme */}
                {task.deadline && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography 
                      variant="caption" 
                      color="primary"
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        fontWeight: 'medium'
                      }}
                    >
                      <TodayIcon fontSize="small" sx={{ mr: 0.5, fontSize: 14 }} />
                      {t('tasks.taskDate', 'Görev Tarihi')}: {task.deadline}
                    </Typography>
                  </Box>
                )}

                {/* Tarih bilgisi göster */}
                {(task.status === 'completed' || task.status === 'missed') && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'flex-end',
                        fontStyle: 'italic'
                      }}
                    >
                      {task.status === 'completed' ? t('tasks.completionDate', 'Tamamlanma') + ': ' : t('tasks.missedTime', 'Kaçırılma') + ': '}
                      {task.fromDatabase && (task.completionDate || task.missedDate)
                        ? `${task.status === 'completed' ? task.completionDate : task.missedDate}`
                        : (task.status === 'completed' 
                            ? (task.completedAt ? new Date(task.completedAt).toLocaleDateString('tr-TR', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-') 
                            : (task.missedAt ? new Date(task.missedAt).toLocaleDateString('tr-TR', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-')
                        )
                      }
                    </Typography>
                  </Box>
                )}

                {task.branchesId && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      fontSize: '0.7rem'
                    }}>
                      <BusinessIcon fontSize="small" sx={{ mr: 0.5, fontSize: 14, color: 'info.main' }} />
                      {t('tasks.branch', 'Şube')}: {getBranchName(task.branchesId)}
                    </Typography>
                  </Box>
                )}

                {/* Tekrarlı görevler için zaman bilgisi göster */}
                {task.isRecurring && task.repetitionTimes && task.repetitionTimes.length > 0 && getTaskTimeColor && (
                  <Box sx={{ mb: 1.5 }}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip 
                          label={`${task.startTolerance || 15} ${t('tasks.minutes', 'dk')} ${t('tasks.tolerance', 'tolerans')}`}
                          size="small"
                          sx={{ 
                            mr: 1,
                            height: 18, 
                            fontSize: '0.65rem', 
                            bgcolor: 'primary.50', 
                            color: 'primary.main',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                        {t('tasks.taskTimes', 'Tekrar saatleri')}:
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {task.repetitionTimes.map((time: string, index: number) => (
                        <TaskTimeChip
                          key={index}
                          label={time}
                          size="small"
                          status={getTaskTimeColor(task, time)}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Gecikme açıklaması butonu - Normal görevlerde ve wasDelayed veya delayReason varsa */}
                {taskType === 'normal' && (task.wasDelayed || task.delayReason) && (
                  <Box sx={{ mb: 1.5 }}>
                    <Button
                      size="small"
                      startIcon={<InfoIcon />}
                      variant="outlined"
                      color="error"
                      fullWidth
                      onClick={(e) => handleOpenDelayReasonModal(task.delayReason || '', e)}
                      sx={{ 
                        p: 0.5, 
                        fontSize: '0.7rem',
                        height: 26
                      }}
                    >
                      {t('tasks.delayReason', 'Gecikme Açıklaması')}
                    </Button>
                  </Box>
                )}

                {/* Reddedilen görev için Red Nedeni ve Görevi Yenile butonları */}
                {taskType === 'normal' && task.status === 'rejected' && (
                  <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Divider sx={{ my: 1 }} />
                    
                    {/* Red Nedeni butonu */}
                    {task.rejectReason && (
                      <Button
                        size="small"
                        startIcon={<CancelIcon />}
                        variant="outlined"
                        color="error"
                        fullWidth
                        onClick={(e) => handleOpenRejectReasonModal(task.rejectReason || '', e)}
                        sx={{ 
                          p: 0.5, 
                          fontSize: '0.7rem',
                          height: 26
                        }}
                      >
                        {t('tasks.rejectReason', 'Red Nedeni')}
                      </Button>
                    )}
                    
                    {/* Görevi Yenile butonu */}
                    {onRenewTask && (
                      <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={(e) => handleOpenRenewTaskModal(task, e)}
                        sx={{ 
                          p: 0.5, 
                          fontSize: '0.7rem',
                          height: 26
                        }}
                      >
                        {t('tasks.renewTask', 'Görevi Yenile')}
                      </Button>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                  {task.personnelName ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {task.personnelName}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="error" fontStyle="italic">
                      {t('tasks.unassigned', 'Personel atanmamış')}
                    </Typography>
                  )}
                  
                  {task.completionType === 'qr' && !task.fromDatabase && task.status !== 'completed' && task.status !== 'missed' && (
                    <Button
                      size="small"
                      startIcon={<QrCodeIcon />}
                      variant="outlined"
                      color="primary"
                      onClick={(e) => onOpenQrPrintModal(task, e)}
                      sx={{ ml: 'auto', minWidth: 'auto', fontSize: '0.75rem', py: 0.5 }}
                    >
                      {t('tasks.printQR', 'QR Yazdır')}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </TaskCard>
          </Grid>
        ))}
      </Grid>

      {/* Gecikme Açıklaması Modalı */}
      <Dialog
        open={delayReasonModalOpen}
        onClose={handleCloseDelayReasonModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
          {t('tasks.delayReason', 'Gecikme Açıklaması')}
          <IconButton
            aria-label="kapat"
            onClick={handleCloseDelayReasonModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'error.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {selectedDelayReason || t('tasks.noDescription', 'Açıklama bulunamadı.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelayReasonModal} color="primary">
            {t('tasks.close', 'Kapat')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Red Nedeni Modalı */}
      <Dialog
        open={rejectReasonModalOpen}
        onClose={handleCloseRejectReasonModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.light', 
          color: 'error.contrastText',
          display: 'flex',
          alignItems: 'center'
        }}>
          <CancelIcon sx={{ mr: 1 }} />
          {t('tasks.rejectReasonModal.title', 'Görev Reddetme Nedeni')}
          <IconButton
            aria-label="kapat"
            onClick={handleCloseRejectReasonModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'error.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1">
            {selectedRejectReason || t('tasks.noDescription', 'Açıklama bulunamadı.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectReasonModal} color="primary">
            {t('tasks.close', 'Kapat')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Görev Yenileme Onay Modalı */}
      <Dialog
        open={renewTaskModalOpen}
        onClose={handleCloseRenewTaskModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.light', 
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center'
        }}>
          <RefreshIcon sx={{ mr: 1 }} />
          {t('tasks.renewTask', 'Görevi Yenile')}
          <IconButton
            aria-label="kapat"
            onClick={handleCloseRenewTaskModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 1 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            {selectedTaskToRenew?.name || 'Görev'}
          </Typography>
          
          <Box sx={{ bgcolor: '#fff5f7', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              {t('tasks.rejectReasonModal.reason', 'Red Nedeni')}:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {selectedTaskToRenew?.rejectReason || t('tasks.notSpecified', 'Belirtilmemiş')}
            </Typography>
          </Box>
          
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t('tasks.renewTaskConfirmation', 'Personel bu görevi reddetmiştir. Görevi personele yeniden göndermek istediğinizden emin misiniz?')}
            <Typography component="span" fontWeight="bold" color="primary.main"> {t('tasks.cannotRejectAgain', 'Personel bu görevi yeniden reddedemeyecek.')}</Typography>
          </Typography>
          
          <TextField
            margin="normal"
            fullWidth
            label={t('tasks.noteForPersonnel', 'Açıklama (Personele İletilecek)')}
            multiline
            rows={3}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder={t('tasks.noteForPersonnelPlaceholder', 'Personele iletmek istediğiniz bir açıklama yazabilirsiniz...')}
            variant="outlined"
            required
            error={adminNote.trim() === ''}
            helperText={adminNote.trim() === '' ? t('tasks.noteRequired', 'Personele iletilecek açıklama gerekli') : ""}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseRenewTaskModal} 
            color="inherit"
            disabled={isRenewing}
          >
            {t('tasks.cancel', 'İptal')}
          </Button>
          <Button 
            onClick={handleRenewTask} 
            variant="contained" 
            color="primary"
            disabled={isRenewing || adminNote.trim() === ''}
            startIcon={isRenewing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {isRenewing ? t('tasks.renewing', 'Yenileniyor...') : t('tasks.renewTask', 'Görevi Yenile')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TaskGrid; 